import { Component } from '@theme/component';

const TEASER_HIDDEN_KEY = 'despina-popup-teaser-hidden';
const SUBSCRIBED_KEY = 'despina-popup-subscribed';
const AUTO_OPENED_KEY = 'despina-popup-auto-opened';
const SIGN_IN_PENDING_KEY = 'despina-popup-sign-in-pending';
const EMAIL_KEY = 'despina-popup-email';
const TEASER_HIDE_DAYS = 7;
const TEASER_DELAY_MS = 1200;
/** Long enough to read the "last step" note and for the store's pixels to send the sign-up */
const SIGN_IN_DELAY_MS = 1500;

/**
 * Storage can be unavailable (private mode, blocked site data): treat it as empty
 * @param {() => Storage} area
 */
const createStorage = (area) => ({
  /** @param {string} key */
  get(key) {
    try {
      return area().getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {string} key @param {string} value */
  set(key, value) {
    try {
      area().setItem(key, value);
    } catch {
      // Not persisting is fine
    }
  },
  /** @param {string} key */
  remove(key) {
    try {
      area().removeItem(key);
    } catch {
      // Nothing to clean up
    }
  },
});

const storage = createStorage(() => localStorage);
/** The typed email only needs to survive the form's own page reload, in this tab */
const tabStorage = createStorage(() => sessionStorage);

/**
 * Membership pop-up: a corner tab that opens a modal <dialog> with the sign-up form. The form posts
 * normally (Shopify customer form); when the page comes back with this form's result, the dialog
 * reopens to show the error, or the "last step" note and then moves on to the customer account
 * sign-in with the email filled in. Back on the store as a member, the dialog opens once with the
 * discount code. The tab stays hidden for a week after it is closed.
 *
 * @typedef {object} NewsletterPopupRefs
 * @property {HTMLElement} [teaser] - The bottom-left tab
 * @property {HTMLDialogElement} [dialog] - The pop-up
 * @property {HTMLElement} [state] - Carries the state ('success' | 'error' | 'member' | '')
 * @property {HTMLElement} [code] - The discount code text
 * @property {HTMLAnchorElement} [signIn] - The link to the customer account sign-in
 *
 * @extends {Component<NewsletterPopupRefs>}
 */
class NewsletterPopupComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[]} */
  #timers = [];

  /** @type {number | undefined} */
  #signInTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog, state } = this.refs;

    const result = state?.dataset.state ?? '';
    const postedHere = window.location.hash === `#${this.dataset.formId}`;
    // Tell the store's pixels about a new sign-up once, not again when the thank-you page is reloaded
    if (result === 'success' && postedHere && !storage.get(SUBSCRIBED_KEY)) {
      window.Shopify?.analytics?.publish?.('despina:newsletter_signup', { source: 'popup' });
    }
    if (result === 'success') storage.set(SUBSCRIBED_KEY, '1');

    // Back from the sign-in this pop-up sent the visitor to: show the code once
    const justJoined = result === 'member' && storage.get(SIGN_IN_PENDING_KEY);
    if (result === 'member') storage.remove(SIGN_IN_PENDING_KEY);

    if (justJoined) {
      this.open();
    } else if (result && result !== 'member' && postedHere) {
      this.open();
      if (result === 'success') this.#scheduleSignIn();
    } else {
      this.#timers.push(window.setTimeout(() => this.#showTeaser(), TEASER_DELAY_MS));
      if (result !== 'member') this.#scheduleAutoOpen();
    }

    // Keep the typed email for the sign-in link the page shows after the form posts
    const form = this.querySelector('form');
    form?.addEventListener(
      'submit',
      () => {
        const email = form.elements.namedItem('contact[email]');
        if (email instanceof HTMLInputElement) tabStorage.set(EMAIL_KEY, email.value.trim());
      },
      { signal }
    );

    // Close on backdrop click (the dialog element itself is the backdrop area)
    dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.close();
      },
      { signal }
    );
    dialog?.addEventListener(
      'close',
      () => {
        // Closing the "last step" note means the visitor wants to stay on the page
        clearTimeout(this.#signInTimer);
        this.#showTeaser();
      },
      { signal }
    );

    // Theme editor: open while the section is selected
    document.addEventListener(
      'shopify:section:select',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.open();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId) this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#timers.forEach((timer) => clearTimeout(timer));
    clearTimeout(this.#signInTimer);
  }

  open() {
    const { dialog, teaser } = this.refs;
    if (!dialog || dialog.open) return;
    if (teaser) teaser.hidden = true;
    dialog.showModal();
  }

  close() {
    this.refs.dialog?.close();
  }

  /** Hides the corner tab for a week */
  dismissTeaser() {
    const { teaser } = this.refs;
    if (teaser) teaser.hidden = true;
    storage.set(TEASER_HIDDEN_KEY, String(Date.now()));
  }

  /** Remembers that this pop-up started the sign-in, so the code shows once the visitor is back */
  signIn() {
    clearTimeout(this.#signInTimer);
    storage.set(SIGN_IN_PENDING_KEY, '1');
  }

  /** @param {MouseEvent} event */
  async copyCode(event) {
    const button = event.target;
    const code = this.refs.code?.textContent?.trim();
    if (!code || !(button instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
      const original = button.textContent;
      button.textContent = button.dataset.copied ?? original;
      this.#timers.push(window.setTimeout(() => (button.textContent = original), 2000));
    } catch {
      // Clipboard can be blocked; the code stays visible to copy by hand
    }
  }

  /** Fills in the email on the sign-in link when the page didn't, then follows it after a moment */
  #scheduleSignIn() {
    const { signIn } = this.refs;
    const email = tabStorage.get(EMAIL_KEY);
    tabStorage.remove(EMAIL_KEY);
    if (!signIn) return;

    const url = new URL(signIn.href);
    if (email && !url.searchParams.has('login_hint')) {
      url.searchParams.set('login_hint', email);
      signIn.href = url.toString();
    }

    this.#signInTimer = window.setTimeout(() => {
      this.signIn();
      window.location.assign(signIn.href);
    }, SIGN_IN_DELAY_MS);
  }

  #showTeaser() {
    const { teaser, dialog } = this.refs;
    if (!teaser || dialog?.open) return;
    const hiddenAt = Number(storage.get(TEASER_HIDDEN_KEY));
    if (hiddenAt && Date.now() - hiddenAt < TEASER_HIDE_DAYS * 864e5) return;
    teaser.hidden = false;
  }

  #scheduleAutoOpen() {
    const delay = Number(this.dataset.autoOpen);
    if (!delay || storage.get(SUBSCRIBED_KEY) || storage.get(AUTO_OPENED_KEY)) return;
    this.#timers.push(
      window.setTimeout(() => {
        storage.set(AUTO_OPENED_KEY, '1');
        this.open();
      }, delay * 1000)
    );
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopupComponent);
}
